import { api } from '@forbiz/web-kit'
import type {
  CatalogResponse,
  CompanyHomeResponse,
  CreateOrderRequest,
  OrderDetailResponse,
  OrderHistoryResponse,
  OrderListResponse,
  OrderResponse,
  OrderStatsResponse,
  OrderSummary,
  OrderTransitionRequest,
  SavedAddressListResponse,
  VendorSettlementResponse,
  VendorSummary,
} from '@forbiz/contracts'

/**
 * 주문 API 클라이언트. 기업 주문서(BIZ-9)와 목록·상세(BIZ-10)가 공유하는 seam이다.
 * 응답 타입은 계약(@forbiz/contracts)에서 가져온다 — BE·FE가 같은 정의를 본다.
 */

export interface OrderListParams {
  status?: string
  companyStatus?: string
  q?: string
  occasionType?: string
  from?: string
  to?: string
  companyId?: number
  /** 목록 정렬 키·방향(BIZ-100 화이트리스트). 미지정 시 서버 기본(주문번호·생성일 내림차순). */
  sort?: 'orderNo' | 'company' | 'desiredDate' | 'amount' | 'createdAt'
  dir?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

function qs(params: OrderListParams): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export const ordersApi = {
  /** 기업 카탈로그(주문 가능한 상품). 공급가·내부 정보는 없다. */
  catalog: (companyId: number) => api.get<CatalogResponse>(`/api/companies/${companyId}/catalog`),
  /** 주문 생성. submit=true면 즉시 접수(발번·스냅샷). */
  create: (companyId: number, body: CreateOrderRequest) =>
    api.post<{ order: OrderResponse }>(`/api/companies/${companyId}/orders`, body),
  /** 임시저장 접수. */
  submit: (orderId: number) => api.post<{ order: OrderResponse }>(`/api/orders/${orderId}/submit`),
  /** 임시저장 수정(이어서 작성). submit=true면 그대로 접수된다. */
  updateDraft: (orderId: number, body: CreateOrderRequest) =>
    api.put<{ order: OrderResponse }>(`/api/orders/${orderId}/draft`, body),
  /** 접수된 주문 본문 수정(기업 셀프편집 — 발주 전까지). 발주 후 변경은 전화(운영자). PUT /api/orders/:id/body. */
  amendBody: (orderId: number, body: CreateOrderRequest) =>
    api.put<{ order: OrderResponse }>(`/api/orders/${orderId}/body`, body),
  /** 주문 목록(역할별 범위·필드는 서버가 건다). */
  list: (params: OrderListParams = {}) => api.get<OrderListResponse>(`/api/orders${qs(params)}`),
  /** 목록 상단 요약 카운트(기업 전용). */
  summary: () => api.get<OrderSummary>('/api/orders/summary'),
  /** 통계 탭 집계(기업 전용). 기간(희망일 from·to) 내 집계 + 거래처별·수신자별. BIZ-46/91. */
  stats: (params: { from?: string; to?: string } = {}) =>
    api.get<OrderStatsResponse>(`/api/orders/stats${qs(params)}`),
  /** 기업 담당자 홈 대시보드(지금 챙길 것·최근·자주 보낸 곳). BIZ-44. */
  home: () => api.get<CompanyHomeResponse>('/api/orders/home'),
  /** 최근 배송지(기업 전용). 접수된 주문의 배송지를 최근순으로 되불러 주문서를 채운다(BIZ-29). */
  savedAddresses: () => api.get<SavedAddressListResponse>('/api/orders/saved-addresses'),
  /** 목록 상단 요약 카운트(업체 전용). 접수 대기·배송 진행·이번 달 완료. */
  vendorSummary: () => api.get<VendorSummary>('/api/orders/vendor-summary'),

  vendorSettlement: (month?: string) =>
    api.get<VendorSettlementResponse>(`/api/orders/vendor-settlement${month ? `?month=${month}` : ''}`),
  /** 주문 상세(역할별 필드는 서버가 건다). */
  detail: (orderId: number) => api.get<{ order: OrderDetailResponse }>(`/api/orders/${orderId}`),
  /** 상태 전이(업체·운영자). 허용 여부는 서버가 최종 판정한다. */
  transition: (orderId: number, body: OrderTransitionRequest) =>
    api.post<{ order: OrderDetailResponse }>(`/api/orders/${orderId}/transitions`, body),
  /** 처리 이력 타임라인. 역할별 노출은 서버가 렌더한다(기업=파생 상태, 업체=발주 이후). */
  history: (orderId: number) => api.get<OrderHistoryResponse>(`/api/orders/${orderId}/history`),
}
